/**
 * Sidebar search « Rechercher un client, une déclaration… » (no behaviour drawn on the boards): results menu
 * under the field (GET /admin/search, ≥ 2 characters, 250 ms debounce), ↑ ↓ Entrée, Échap closes.
 * Collapsed sidebar: an icon that unfolds the sidebar.
 */
import { useEffect, useId, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Search } from "lucide-react";
import { useApiQuery } from "@/api/hooks";
import { shellApi } from "@/contracts/shell";
import { cn } from "@/lib/cn";

const KIND_LABEL = { company: "Client", creation_dossier: "Création", declaration: "Déclaration", staff: "Équipe" } as const;

export function SidebarSearch({ collapsed, onExpand, onNavigate }: { collapsed: boolean; onExpand?: () => void; onNavigate?: () => void }) {
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  const [open, setOpen] = useState(false);
  const [cursor, setCursor] = useState(0);
  const navigate = useNavigate();
  const listId = useId();
  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);
  const enabled = debounced.length >= 2;
  const res = useApiQuery(shellApi.search, { query: { q: debounced } }, { enabled, staleTime: 30_000 });
  const hits = enabled ? (res.data?.hits ?? []) : [];

  if (collapsed) {
    return (
      <div className="flex justify-center pb-3">
        <button type="button" onClick={onExpand} aria-label="Rechercher" title="Rechercher" className="inline-flex size-10 items-center justify-center rounded-[10px] bg-white/[.07] text-shell-icon hover:text-white">
          <Search className="size-[15px]" />
        </button>
      </div>
    );
  }

  const go = (href: string) => {
    setOpen(false);
    setQ("");
    onNavigate?.();
    void navigate({ href });
  };

  return (
    <div className="relative z-10 px-2.5 pb-3">
      {/* The placeholder is a wrapping label (the board shows it on two lines in the 248 px sidebar). */}
      <label className="relative flex min-h-[38px] items-center gap-2 rounded-[10px] bg-white/[.07] px-2.5 py-1.5 focus-within:ring-2 focus-within:ring-primary-300/60">
        <Search className="size-[15px] shrink-0 text-shell-icon" aria-hidden />
        <span className="relative grid min-w-0 flex-1">
          {!q && (
            <span aria-hidden className="pointer-events-none col-start-1 row-start-1 self-center text-[12px] leading-[1.25] text-shell-text">
              Rechercher un client, une déclaration…
            </span>
          )}
          <input
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setOpen(true);
              setCursor(0);
            }}
            onFocus={() => setOpen(true)}
            onBlur={() => setTimeout(() => setOpen(false), 150)}
            onKeyDown={(e) => {
              if (e.key === "Escape") setOpen(false);
              if (e.key === "ArrowDown") setCursor((c) => Math.min(c + 1, hits.length - 1));
              if (e.key === "ArrowUp") setCursor((c) => Math.max(c - 1, 0));
              if (e.key === "Enter" && hits[cursor]) go(hits[cursor].href);
            }}
            role="combobox"
            aria-expanded={open && enabled}
            aria-controls={listId}
            aria-label="Rechercher un client, une déclaration"
            className="col-start-1 row-start-1 h-full min-h-[22px] min-w-0 bg-transparent text-[12px] text-white outline-none"
          />
        </span>
      </label>
      {open && enabled && (
        <div
          id={listId}
          role="listbox"
          className="absolute left-2.5 top-[calc(100%-6px)] w-[min(360px,calc(100vw-20px))] overflow-hidden rounded-[14px] border border-line bg-white py-1.5 text-ink shadow-flyout"
        >
          {res.isLoading && <div className="px-3.5 py-2.5 text-[12.5px] text-muted">Recherche…</div>}
          {!res.isLoading && hits.length === 0 && <div className="px-3.5 py-2.5 text-[12.5px] text-muted">Aucun résultat pour « {debounced} ».</div>}
          {hits.map((h, i) => (
            <button
              key={`${h.kind}-${h.id}`}
              type="button"
              role="option"
              aria-selected={i === cursor}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => go(h.href)}
              className={cn("flex w-full items-start gap-2.5 px-3.5 py-2 text-left", i === cursor ? "bg-primary-50" : "hover:bg-canvas")}
            >
              <span className="mt-0.5 shrink-0 rounded-md bg-neutral px-1.5 py-px text-[10px] font-bold uppercase tracking-[0.04em] text-ink-2">{KIND_LABEL[h.kind]}</span>
              <span className="min-w-0">
                <span className="block break-words text-[13px] font-semibold">{h.label}</span>
                {h.sublabel && <span className="block break-words text-[11.5px] text-muted">{h.sublabel}</span>}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
